import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { alpha, useTheme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import SVGInline from "react-svg-inline";
import { SegmentFilter, useSegmentFilters } from '../segmentFilters';
import SegmentItem, { StatItemProps } from './SegmentItem';
import { DataH5, DataLabel11, DataLabel12, WIDGET_COLORS } from './WidgetText';

// Re-exported so widgets keep importing every primitive from one module.
export type { StatItemProps } from './SegmentItem';
export { DataH5, DataLabel10, DataLabel11, DataLabel12, WIDGET_COLORS } from './WidgetText';


// Needs to be updated - retrieved from Educator App??
//export const educatorPassportIcon = require('/icon/passport-icon.svg');
export const educatorsIcon = require('./icons/educators-icon.svg.raw');
export const vcmIcon = require('./icons/vcm-icon.svg.raw');
export const profilingIcon = require('./icons/profile-icon.svg.raw');
export const currencyIcon = require('./icons/currency-icon.svg.raw');
export const registrationIcon = require('./icons/registration-icon.svg.raw');


const GRID_COLUMNS = 12;
const MAX_EXPAND_PER_WIDGET = 2;

// What a widget's totals and segment counts are counting. Supplied in its
// plural form; a trailing "s" is dropped for a count of one.
export const DEFAULT_UNIT = 'users';

export const formatUnit = (count: number, unit: string) =>
  `${count} ${count === 1 && unit.endsWith('s') ? unit.slice(0, -1) : unit}`;

// Greedily packs widgets into rows of `GRID_COLUMNS` width, in order, without
// splitting a widget across rows. Mirrors a simple bin-packing pass rather
// than a fixed widget-count-per-row chunking, so rows reflect actual widths.
export const packWidgetRows = <T,>(items: T[], getWidth: (item: T) => number): T[][] => {
  const rows: T[][] = [];
  let row: T[] = [];
  let rowWidth = 0;

  items.forEach((item) => {
    const width = getWidth(item);
    if (row.length > 0 && rowWidth + width > GRID_COLUMNS) {
      rows.push(row);
      row = [];
      rowWidth = 0;
    }
    row.push(item);
    rowWidth += width;
  });
  if (row.length > 0) rows.push(row);

  return rows;
};

// Expands a row's widget widths to fill the remaining space, but only when the
// leftover space is small relative to the row (each widget can grow by at most
// `MAX_EXPAND_PER_WIDGET`). If the row is too sparse to expand tidily, leave
// the gap as blank space rather than stretching widgets thin.
export const expandRowWidths = (baseWidths: number[]): number[] => {
  const rowWidth = baseWidths.reduce((sum, width) => sum + width, 0);
  const slack = GRID_COLUMNS - rowWidth;
  const maxExpand = baseWidths.length * MAX_EXPAND_PER_WIDGET;

  if (slack <= 0 || slack > maxExpand) {
    return baseWidths;
  }

  const expanded = [...baseWidths];
  // Widen the widest widgets first, round-robin, so they absorb slack
  // before narrower ones do.
  const order = expanded.map((_, index) => index).sort((a, b) => expanded[b] - expanded[a]);

  let remaining = slack;
  let cursor = 0;
  while (remaining > 0) {
    const index = order[cursor % order.length];
    if (expanded[index] < baseWidths[index] + MAX_EXPAND_PER_WIDGET) {
      expanded[index] += 1;
      remaining -= 1;
    }
    cursor += 1;
  }

  return expanded;
};

export interface TooltipDefinition {
  // The bolded term being defined (e.g. "Complete"), coloured to match the bar
  // segment and stat it refers to. Left uncoloured it falls back to the same
  // grey SegmentItem gives an uncoloured stat, so the two stay in step.
  term: string;
  description: string;
  color?: string;
}

interface WidgetTooltipProps {
  heading?: string;
  // An array renders as separate paragraphs, for widgets whose intro needs a
  // second one (e.g. what Program Registrations' total is actually counting).
  description?: string | string[];
  definitions?: TooltipDefinition[];
  // Italicised footer, for caveats such as the supervision scope note.
  note?: string;
}

// Structured alternative to passing `tooltip` a plain string. Keeps the
// bold/coloured/italic treatment in one place so every widget's tooltip reads
// the same, rather than each one hand-rolling its own JSX.
export const WidgetTooltip = ({ heading, description, definitions, note }: WidgetTooltipProps) => (
  <div style={{ lineHeight: 1.5 }}>
    {heading ? <div style={{ fontWeight: 700 }}>{heading}</div> : null}
    {/* The first paragraph sits directly under the heading; any that follow are
        spaced off it the same way the definitions and note are. */}
    {(Array.isArray(description) ? description : [description]).map((paragraph, index) =>
      paragraph ? (
        <div key={index} style={index > 0 ? { marginTop: 8 } : undefined}>
          {paragraph}
        </div>
      ) : null
    )}
    {definitions?.length ? (
      <div style={{ marginTop: 8 }}>
        {definitions.map(({ term, description: definition, color = WIDGET_COLORS.gray }) => (
          <div key={term}>
            <span style={{ fontWeight: 700, color }}>{term}:</span> {definition}
          </div>
        ))}
      </div>
    ) : null}
    {note ? <div style={{ marginTop: 8, fontStyle: 'italic' }}>{note}</div> : null}
  </div>
);

interface WidgetCardProps {
  title: string;
  total?: number;
  unit?: string;
  // Overrides the default `Total {unit}` label under the total, for units whose
  // name is too long to sit alongside "Total" (e.g. "Registrations").
  totalLabel?: string;
  // Either a plain string (rendered with its line breaks preserved) or a
  // `WidgetTooltip` for content that needs colour, weight or emphasis.
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
  isRoundedIcon?: boolean;
  children: React.ReactNode;
}

export const WidgetCard = ({
  title,
  total,
  unit = DEFAULT_UNIT,
  totalLabel,
  tooltip,
  children,
  icon,
  isRoundedIcon
}: WidgetCardProps) => {
  const theme = useTheme();

  return (
    <Paper style={{ minHeight: '100%', boxSizing: 'border-box', padding: 16 }}>
      {/* `baseline` sits the total's number on the same line as the title text. The
          icon is kept inline within the title so the row's baseline comes from the
          title text rather than the icon's box. */}
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 24 }}>
        <Typography style={{ fontSize: 13, fontWeight: 500, marginRight: 8 }}>
          {icon ? (
            <span style={{
              display: 'inline-flex',
              verticalAlign: 'middle',
              marginRight: 8,
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              width: 24,
              height: 24,
              padding: 4,
              borderRadius: isRoundedIcon ? '50%' : 4,
              border: `1px solid ${WIDGET_COLORS.lightGray}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }}>
              <SVGInline
                width="16"
                height="16"
                style={{ display: 'inline-block', position: 'relative', top: 2, color: theme.palette.primary.main }}
                svg={icon}
              />
            </span>
          ) : null}
          {title}
        </Typography>
        {tooltip ? (
          // `pre-line` keeps the blank lines and per-definition line breaks the
          // widgets' TOOLTIP strings are written with, while still wrapping long
          // lines; without it the whole thing collapses into one block of text.
          // The light surface replaces MUI's default dark grey, which the
          // segment colours used by `WidgetTooltip` are illegible against.
          <Tooltip
            title={tooltip}
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'pre-line',
                  maxWidth: 360,
                  padding: '10px 12px',
                  fontSize: 12,
                  backgroundColor: '#FFFFFF',
                  color: WIDGET_COLORS.dark,
                  border: `1px solid ${WIDGET_COLORS.lightGray}`,
                  boxShadow: 3
                }
              }
            }}
          >
            {/* The row is baseline-aligned, which sits the icon's box entirely
                above the title's baseline. Nudge it down so its centre lines up
                with the title text rather than floating above it. */}
            <InfoOutlineIcon
              style={{ fontSize: 18, color: WIDGET_COLORS.gray, position: 'relative', top: 4 }}
            />
          </Tooltip>
        ) : null}
        <div style={{ flexGrow: 1 }} />
        {total !== undefined && (
          <div style={{ textAlign: 'right' }}>
            <DataH5 inline={false} lineHeight={1.1}>{total}</DataH5>
            <DataLabel11>{totalLabel ?? `Total ${unit}`}</DataLabel11>
          </div>
        )}
      </div>
      {children}
    </Paper>
  )
};

interface BarSegment {
  percent: number;
  color: string;
  // A tooltip is only rendered for segments that supply a label.
  label?: string;
  count?: number;
  // Overrides `percent` in the tooltip. Needed where the bar widths are scaled
  // for presentation rather than being the literal percentage (see VcmWidget).
  displayPercent?: number;
  // Same filter as the segment's stat below the bar, so clicking either toggles
  // the same thing.
  filter?: SegmentFilter;
}

// Tooltip for the leftover, unfilled portion of the bar.
interface BarRemainder {
  label: string;
  count?: number;
  percent?: number;
  filter?: SegmentFilter;
}

interface SegmentedBarProps {
  segments: BarSegment[];
  remainder?: BarRemainder;
  unit?: string;
  trackColor?: string;
  height?: number;
  gap?: number;
}

const SegmentTooltipTitle = ({
  label,
  percent,
  count,
  unit
}: { label: string; percent: number; count?: number; unit: string }) => (
  <div style={{ textAlign: 'left', lineHeight: 1.5 }}>
    <div>{label}</div>
    <div>{percent}%</div>
    {count !== undefined && <div>{formatUnit(count, unit)}</div>}
  </div>
);

export const SegmentedBar = ({
  segments,
  remainder,
  unit = DEFAULT_UNIT,
  trackColor = WIDGET_COLORS.lightGray,
  height = 12,
  gap = 2
}: SegmentedBarProps) => {
  const segmentFilters = useSegmentFilters();
  const visibleSegments = segments.filter((segment) => segment.percent > 0);
  const remaining = Math.max(
    100 - visibleSegments.reduce((sum, segment) => sum + segment.percent, 0),
    0
  );

  const bar = (
    flexGrow: number,
    color: string,
    tooltip?: { label: string; percent: number; count?: number },
    key?: number,
    filter?: SegmentFilter
  ) => {
    const isClickable = Boolean(filter && segmentFilters);

    const block = (
      <div
        key={key}
        // A filtered bar segment toggles its own filter, which shows up on the
        // stat below rather than on the bar - the bar always reads as the plain
        // breakdown. The stat is the keyboard accessible control for the same
        // filter, so the bar deliberately stays out of the tab order rather
        // than duplicating the tab stop.
        onClick={isClickable ? () => segmentFilters?.toggle(filter as SegmentFilter) : undefined}
        style={{
          flex: `${flexGrow} 1 0%`,
          minWidth: 0,
          borderRadius: height / 2,
          backgroundColor: color,
          cursor: isClickable ? 'pointer' : 'default'
        }}
      />
    );

    if (!tooltip) return block;

    return (
      <Tooltip
        key={key}
        arrow
        placement="top"
        title={
          <SegmentTooltipTitle
            label={tooltip.label}
            percent={tooltip.percent}
            count={tooltip.count}
            unit={unit}
          />
        }
      >
        {block}
      </Tooltip>
    );
  };

  return (
    <div style={{ display: 'flex', width: '100%', height, gap }}>
      {visibleSegments.map((segment, index) =>
        bar(
          segment.percent,
          segment.color,
          segment.label
            ? {
                label: segment.label,
                percent: segment.displayPercent ?? segment.percent,
                count: segment.count
              }
            : undefined,
          index,
          segment.filter
        )
      )}
      {remaining > 0 &&
        bar(
          remaining,
          trackColor,
          remainder
            ? { label: remainder.label, percent: remainder.percent ?? remaining, count: remainder.count }
            : undefined,
          undefined,
          remainder?.filter
        )}
    </div>
  );
};

export const StatRow = ({ items }: { items: StatItemProps[] }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', margin: '16px 0', borderTop: '1px solid #F5F5F5', justifyContent: 'space-between' }}>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && <div style={{ flex: '0 0 1px', margin: '8px 4px 0 4px', backgroundColor: '#F5F5F5' }} />}
        <SegmentItem {...item} />
      </React.Fragment>
    ))}
  </div>
);

export const SingleStat = ({ value, label, showTopBorder = true }: { value: string; label: string; showTopBorder?: boolean }) => (

  <div style={{
    paddingTop: 12,
    marginTop: showTopBorder ? 12 : 0,
    borderTop: showTopBorder ? '1px solid #F5F5F5' : 'none'
  }}>
    <DataH5>
      {value}
    </DataH5>{' '}
    <DataLabel12 >
      {label}
    </DataLabel12>
  </div>

);

